import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLOR = {
  pending: "bg-[#facc15]",
  processing: "bg-[#00d4ff]",
  shipped: "bg-[#ff1493] text-white",
  cancelled: "bg-black text-white",
};

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders/mine").then((r) => {
      setOrders(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div data-testid="account-page" className="min-h-screen">
      <section className="border-b-[3px] border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono-ui text-xs text-[#ff1493]">— MY ACCOUNT</div>
            <h1 className="font-display text-5xl md:text-6xl leading-none mt-3">Hi, {user?.name?.split(" ")[0]}.</h1>
            <div className="font-mono-ui text-xs text-black/60 mt-2">{user?.email}</div>
          </div>
          <button onClick={logout} data-testid="account-logout" className="px-4 py-2 bg-black text-white border-[3px] border-black shadow-brut-sm btn-press font-mono-ui text-xs font-bold self-start">
            SIGN OUT
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="font-display text-3xl mb-6">Your orders</div>
        {loading ? (
          <div className="font-mono-ui text-xs">LOADING...</div>
        ) : orders.length === 0 ? (
          <div className="border-[3px] border-black p-10 text-center font-mono-ui text-xs">
            NO ORDERS YET. BROWSE THE SHOP TO PLACE YOUR FIRST ORDER.
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="border-[3px] border-black bg-white p-5 shadow-brut-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono-ui text-[10px] text-black/50">ORDER</div>
                    <div className="font-display text-lg">#{o.id.slice(0, 8).toUpperCase()}</div>
                  </div>
                  <div>
                    <div className="font-mono-ui text-[10px] text-black/50">DATE</div>
                    <div className="font-mono-ui text-xs">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div>
                    <div className="font-mono-ui text-[10px] text-black/50">TOTAL</div>
                    <div className="font-display text-lg">£{o.total.toFixed(2)}</div>
                  </div>
                  <div className={`px-3 py-1.5 border-[3px] border-black font-mono-ui text-[10px] font-bold ${STATUS_COLOR[o.status]}`}>
                    {o.status.toUpperCase()}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t-2 border-black/10 grid sm:grid-cols-2 gap-4">
                  <div>
                    <div className="font-mono-ui text-[10px] text-black/50 mb-1">ITEMS ({o.items.length})</div>
                    <ul className="text-sm space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="flex justify-between">
                          <span className="truncate pr-3">{it.name} × {it.quantity}</span>
                          <span className="font-mono-ui text-[11px]">£{(it.price * it.quantity).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-mono-ui text-[10px] text-black/50 mb-1">SHIPPING TO</div>
                    <div className="text-sm leading-relaxed">
                      {o.shipping.full_name}<br />
                      {o.shipping.address_line1}{o.shipping.address_line2 ? `, ${o.shipping.address_line2}` : ""}<br />
                      {o.shipping.city}, {o.shipping.postcode}<br />
                      {o.shipping.country}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
